import type { DbExecutor } from "./executor";
import type { BidService } from "./bidService";
import type { ProposedCostRepository } from "./repositories/proposedCostRepository";
import type { DesignRepository } from "./repositories/designRepository";
import type { CategoryRepository } from "./repositories/categoryRepository";
import type { ReconciliationService } from "./reconciliationService";
import { categoriesById, effectiveBucket, expenseTotalCents } from "../engine/financials";
import {
  deriveCategoryBudget,
  projectionBasisAdjustment,
  type CategoryBudgetModel,
  type CategoryAuthorizedInput,
} from "../engine/categoryBudget";
import type { TradeId } from "../domain/trade";
import type { Cents } from "../domain/money";
import type { MixedSettlementContext } from "./repositories/laborSettlementRepository";

/**
 * CATEGORY BUDGET ASSEMBLY (spec §74).
 *
 * Gathers what the pure engine needs — the active design's features and
 * lines, the bid model's scopes (so quote coverage resolves through the
 * SAME claiming rule bids use), selected quotes, authorized amounts and
 * the renovation-bucket actual events — and derives the budget on demand.
 * Nothing here stores a total; the only persistence is the authorized
 * amount and variance justification a person wrote.
 */

export class CategoryBudgetService {
  constructor(
    private readonly db: DbExecutor,
    private readonly bids: BidService,
    private readonly proposedCosts: ProposedCostRepository,
    private readonly designs: DesignRepository,
    private readonly categories: CategoryRepository,
    private readonly reconciliation: ReconciliationService,
  ) {}

  /** Reuses authoritative active scope and quote coverage; never apportions a total. */
  async mixedSettlementContext(projectId:string):Promise<MixedSettlementContext> {
    const designId=await this.designs.getActiveDesignId(projectId);
    const [lines,bids,quotes,events,model,categories]=await Promise.all([this.proposedCosts.listForDesign(projectId,designId),this.bids.model(projectId,designId),this.bids.listQuotes(projectId),this.reconciliation.eventsForProject(projectId),this.model(projectId),this.categories.listAll()]);
    return {lines:lines.filter(l=>l.archivedAt===null).map(l=>({id:l.id,featureId:l.featureId})),
      quoteGroups:quotes.filter(q=>q.selectedAt!==null).map(q=>({id:q.id,featureIds:[...new Set(q.coverage.flatMap(key=>bids.scopes.find(s=>s.key===key)?.featureIds??[]))]})),
      authorizedGroups:model.rows.filter(r=>r.authorizedCents!==null).map(r=>({id:r.categoryId,featureIds:r.featureIds})),
      sources:events.filter(e=>effectiveBucket(e,categoriesById(categories))==="renovation").map(e=>({...e,amountCents:expenseTotalCents(e)}))};
  }

  /** Authorized rows only — the cheap existence probe for fast paths. */
  async listAuthorized(projectId: string): Promise<CategoryAuthorizedInput[]> {
    const rows = await this.db.select<{
      category_id: string;
      authorized_cents: number | null;
      variance_justification: string;
    }>("SELECT category_id, authorized_cents, variance_justification FROM category_budgets WHERE project_id = ?", [
      projectId,
    ]);
    return rows.map((r) => ({
      categoryId: r.category_id,
      authorizedCents: r.authorized_cents === null ? null : Number(r.authorized_cents),
      varianceJustification: r.variance_justification ?? "",
    }));
  }

  /**
   * Set (or clear, with null) the authorized amount for a category, and
   * keep its variance justification. A decision, never a derived figure.
   */
  async setAuthorized(
    projectId: string,
    categoryId: string,
    authorizedCents: Cents | null,
    varianceJustification: string,
  ): Promise<void> {
    const now = new Date().toISOString();
    if (authorizedCents === null && varianceJustification.trim() === "") {
      await this.db.execute("DELETE FROM category_budgets WHERE project_id = ? AND category_id = ?", [
        projectId, categoryId,
      ]);
      return;
    }
    const existing = await this.db.select<{ category_id: string }>(
      "SELECT category_id FROM category_budgets WHERE project_id = ? AND category_id = ?",
      [projectId, categoryId],
    );
    if (existing.length === 0) {
      await this.db.execute(
        "INSERT INTO category_budgets (project_id, category_id, authorized_cents, variance_justification, updated_at) VALUES (?, ?, ?, ?, ?)",
        [projectId, categoryId, authorizedCents, varianceJustification, now],
      );
    } else {
      await this.db.execute(
        "UPDATE category_budgets SET authorized_cents = ?, variance_justification = ?, updated_at = ? WHERE project_id = ? AND category_id = ?",
        [authorizedCents, varianceJustification, now, projectId, categoryId],
      );
    }
  }


  /**
   * Rename ONE management work item (spec §78R). Presentation only: the
   * group identity, its features, its money, quotes, actuals and every
   * downstream figure are untouched, and the generated wording is always
   * recomputed beside it so the rename can be undone (label null).
   */
  async setManagementLabel(
    projectId: string,
    categoryId: string,
    groupKey: string,
    label: string | null,
  ): Promise<void> {
    if (label === null || label.trim() === "") {
      await this.db.execute("DELETE FROM budget_line_labels WHERE project_id = ? AND group_key = ?", [
        projectId, groupKey,
      ]);
      return;
    }
    const now = new Date().toISOString();
    const existing = await this.db.select<{ group_key: string }>(
      "SELECT group_key FROM budget_line_labels WHERE project_id = ? AND group_key = ?",
      [projectId, groupKey],
    );
    if (existing.length === 0) {
      await this.db.execute(
        "INSERT INTO budget_line_labels (project_id, group_key, category_id, label, updated_at) VALUES (?, ?, ?, ?, ?)",
        [projectId, groupKey, categoryId, label.trim(), now],
      );
    } else {
      await this.db.execute(
        "UPDATE budget_line_labels SET label = ?, category_id = ?, updated_at = ? WHERE project_id = ? AND group_key = ?",
        [label.trim(), categoryId, now, projectId, groupKey],
      );
    }
  }

  /** The derived category budget for the project's ACTIVE design + common scope. */
  async model(projectId: string): Promise<CategoryBudgetModel> {
    const designId = await this.designs.getActiveDesignId(projectId);
    const [bidModel, featureRows, decisionRows, tradeRows, roomRows, lines, quotes, authorized, events, expenseCategories, labelRows] =
      await Promise.all([
        this.bids.model(projectId, designId),
        this.db.select<{
          id: string; origin_id: string | null; feature_definition_id: string | null; name: string;
          action: string; room_id: string | null; work_status: string | null;
        }>(
          `SELECT id, origin_id, feature_definition_id, name, action, room_id, work_status
             FROM project_features
            WHERE project_id = ? AND archived_at IS NULL AND (design_id IS NULL OR design_id = ?)`,
          [projectId, designId],
        ),
        this.db.select<{ root_id: string; trade_override: string | null }>(
          "SELECT root_id, trade_override FROM bid_decisions WHERE project_id = ? AND (design_id IS ? OR design_id = ?)",
          [projectId, designId, designId],
        ),
        this.db.select<{ trade_id: string; combine_with: string | null }>(
          "SELECT trade_id, combine_with FROM bid_trade_settings WHERE project_id = ? AND (design_id IS ? OR design_id = ?)",
          [projectId, designId, designId],
        ),
        this.db.select<{ id: string; name: string; floor_name: string }>(
          `SELECT r.id, r.name, f.name AS floor_name
             FROM rooms r JOIN floors f ON f.id = r.floor_id
            WHERE f.project_id = ? AND r.archived_at IS NULL`,
          [projectId],
        ),
        this.proposedCosts.listForDesign(projectId, designId),
        this.bids.listQuotes(projectId),
        this.listAuthorized(projectId),
        this.reconciliation.eventsForProject(projectId),
        this.categories.listAll(),
        // Spec §78R: the user's wording for management work items.
        this.db.select<{ group_key: string; label: string }>(
          "SELECT group_key, label FROM budget_line_labels WHERE project_id = ?",
          [projectId],
        ),
      ]);

    const decisions = new Map<string, TradeId>();
    for (const row of decisionRows) {
      if (row.trade_override) decisions.set(row.root_id, row.trade_override as TradeId);
    }
    const combines = new Map<TradeId, TradeId>();
    for (const row of tradeRows) {
      if (row.combine_with && row.combine_with !== row.trade_id) {
        combines.set(row.trade_id as TradeId, row.combine_with as TradeId);
      }
    }

    // Actual RENOVATION money, resolved to a feature where the accounting
    // links one — directly, or through the estimate line it was recorded
    // against. Other buckets (acquisition, financing, holding, selling)
    // are different economics and never enter the renovation budget.
    const byId = categoriesById(expenseCategories);
    const lineFeature = new Map(lines.map((l) => [l.id, l.featureId]));
    const actuals: Array<{ featureId: string | null; amountCents: Cents; sourceId?:string }> = [];
    for (const event of events) {
      if (event.voidedAt !== null) continue;
      if (effectiveBucket(event, byId) !== "renovation") continue;
      const featureId =
        event.linkedFeatureId ??
        (event.linkedProposedLineId ? (lineFeature.get(event.linkedProposedLineId) ?? null) : null);
      actuals.push({ featureId, amountCents: expenseTotalCents(event),sourceId:event.id });
    }

    const mixedRows=await this.db.select<{id:string;description:string;expense_id:string|null;allocation_id:string|null;coverage_snapshot:string|null}>("SELECT id,description,expense_id,allocation_id,coverage_snapshot FROM labor_settlements WHERE project_id=? AND coverage_kind='mixed-work'",[projectId]);
    const mixedSettlements=mixedRows.flatMap(s=>{
      const snapshot=JSON.parse(s.coverage_snapshot??"{}") as {featureIds?:string[];proposedLineIds?:string[]};
      const source=actuals.find(a=>a.sourceId===(s.expense_id??s.allocation_id));
      const covered=new Set(snapshot.proposedLineIds??[]);
      if(lines.some(l=>l.archivedAt===null&&l.featureId&&snapshot.featureIds?.includes(l.featureId)&&!covered.has(l.id)))return [];
      // A voided/missing source cannot suppress a remaining estimate.
      return source&&source.amountCents>0?[{id:s.id,description:s.description,featureIds:snapshot.featureIds??[],sourceId:source.sourceId!,actualCents:source.amountCents}]:[];
    });

    return deriveCategoryBudget({
      features: featureRows.map((row) => ({
        id: row.id,
        rootId: row.origin_id ?? row.id,
        featureDefinitionId: row.feature_definition_id,
        name: row.name,
        action: row.action,
        roomId: row.room_id,
        workStatus: row.work_status,
      })),
      decisions,
      combines,
      lines: lines.map((l) => ({
        featureId: l.featureId,
        amountCents: l.amountCents,
        archived: l.archivedAt !== null,
        description: l.description,
        costType: l.costType,
      })),
      quotes: quotes.map((q) => ({
        id: q.id,
        contractor: q.contractor,
        amountCents: q.amountCents,
        coverage: q.coverage,
        selected: q.selectedAt !== null,
      })),
      scopes: bidModel.scopes.map((s) => ({ key: s.key, featureIds: s.featureIds })),
      authorized,
      actuals,
      mixedSettlements,
      locationLabels: new Map(roomRows.map((r) => [r.id, r.floor_name ? `${r.floor_name} → ${r.name}` : r.name])),
      managementLabels: new Map(labelRows.map((r) => [r.group_key, r.label])),
    });
  }

  /**
   * Phase 12's renovation-basis delta (spec §74S): what the stronger
   * planning truths (selected quotes, authorized budgets) change against
   * the calculated estimate, over open categories. Projects with neither
   * answer with two cheap queries and zero.
   */
  async basisDelta(
    projectId: string,
    projection?: { lines: ReadonlyArray<{ featureId: string | null; proposedCents: Cents; projectedCents: Cents }> },
  ): Promise<Cents> {
    const [quoteRows, authorizedRows, mixedRows] = await Promise.all([
      this.db.select<{ n: number }>(
        "SELECT COUNT(*) AS n FROM contractor_quotes WHERE project_id = ? AND selected_at IS NOT NULL AND archived_at IS NULL",
        [projectId],
      ),
      this.db.select<{ n: number }>(
        "SELECT COUNT(*) AS n FROM category_budgets WHERE project_id = ? AND authorized_cents IS NOT NULL",
        [projectId],
      ),
      this.db.select<{n:number}>("SELECT COUNT(*) AS n FROM labor_settlements WHERE project_id=? AND coverage_kind='mixed-work'",[projectId]),
    ]);
    if (Number(quoteRows[0]?.n ?? 0) === 0 && Number(authorizedRows[0]?.n ?? 0) === 0 && Number(mixedRows[0]?.n??0)===0) return 0;
    const model = await this.model(projectId);
    // Given Phase 12's per-feature projection, replace the work a quote or
    // authorization stands for instead of stacking on it (§88B).
    return projection ? projectionBasisAdjustment(model, projection.lines) : model.basisDeltaCents;
  }
}
